import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import {
  User,
  UserLevel,
  firstnameRules,
  lastnameRules,
  levelRules,
} from '@/models/user.model';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { UsersActions, UsersState } from '@/stores/users.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import UserStories from './UserStories.vue';
import UserBPT from './UserBPT.vue';
import UserLabels from './UserLabels.vue';
import UserNotifications from './UserNotifications.vue';
import UserMessages from './UserMessages.vue';
import UserGoals from './UserGoals.vue';
import UserActions from './UserActions.vue';
import UserInitiatives from './UserInitiatives.vue';
import UserInfoGraphics from './UserInfoGraphics.vue';
import UserCo2Quiz from '@/components/users/UserCo2Quiz.vue';
import UserCommunity from './UserCommunity.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'UserForm',
  props: {
  data: {
    type: Object as PropType<User>,
  },
},
  setup(__props) {

const store = useStore<ShiftCMSState & { cs: UsersState }>();

const props = __props;

const storeUser = computed<User | null>(() => store.state.cs.user);

const valid = ref<boolean>(false);

const user = reactive<User>({
  id: NEW_ID,
  firstName: '',
  lastName: '',
  deviceID: '',
  email: '',
  language: 'nl',
  level: UserLevel.Device,
  actions: [],
  labels: [],
  steps: [],
  stories: [],
  userCo2QuizAnswers: [],
  infographics: [],
  initiatives: [],
  notificationSettings: [],
  communities: [],
});

watch(
  () => storeUser,
  (v) => {
    Object.assign(user, v);
  },
);

const showResetPassword = ref<boolean>(false);
const password = ref<string>('');

function resetPassword() {
  showResetPassword.value = true;
}

async function saveResetPassword() {
  if (user && user.id !== -1) {
    await store.dispatch(UsersActions.ResetPassword, {
      id: user.id,
      password: password.value,
    });
    showResetPassword.value = false;
  }
}

const action = computed<string>(() => (user.id === NEW_ID ? 'Add' : 'Save'));

const saveUser = async () => {
  let res;
  if (valid.value) {
    if (user.id === -1) {
      res = await store.dispatch(UsersActions.AddUser, user);
    } else {
      res = await store.dispatch(UsersActions.UpdateUser, user);
    }
  }
  if (res.status) {
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved user',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(UsersActions.GetUsers);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: res.message,
      type: 'warning',
    });
  }
};

const deleteUser = async () => {
  if (confirm('Are you sure you want to delete this user and all their data')) {
    await store.dispatch(UsersActions.DeleteUser, user);
    store.commit(Mutations.HideDrawer);
    store.dispatch(UsersActions.GetUserList, {
      search: '',
      level: null,
      page: 1,
      count: 25,
      sort: 'id',
      direction: 'asc',
    });
  }
};

const resetAll = async () => {
  if (confirm('Are you sure you want to reset all data for this user')) {
    await store.dispatch(UsersActions.ResetUser, user);
    store.commit(Mutations.HideDrawer);
    store.dispatch(UsersActions.GetUserList, {
      search: '',
      level: null,
      page: 1,
      count: 25,
      sort: 'id',
      direction: 'asc',
    });
  }
};

const resetBPT = async () => {
  if (user.id !== NEW_ID) {
    const u = await store.dispatch(UsersActions.GetFullUser, user.id);

    Object.assign(user, u);
  }
};

const disable2FA = async () => {
  const result = await store.dispatch(UsersActions.Disable2FA, user.id);

  if (result.twoFactorDisabled) {
    const r = store.dispatch(UsersActions.GetUser, user.id);
    Object.assign(user, r);
    store.dispatch(Actions.ShowNotification, {
      message: result.message,
      type: 'success',
    });
  }
};

onMounted(() => {
  if (props.data) {
    Object.assign(user, props.data);

    if (user.id !== NEW_ID) {
      store.dispatch(UsersActions.GetFullUser, user.id);
    }
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_form = _resolveComponent("v-form")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_form, {
      modelValue: valid.value,
      "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((valid).value = $event)),
      onSubmit: _withModifiers(saveUser, ["prevent"])
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_container, null, {
          default: _withCtx(() => [
            _createElementVNode("h2", null, _toDisplayString(_ctx.$t(action.value)) + " a user", 1),
            _createVNode(_component_v_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, { class: "mx-auto" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_text_field, {
                      modelValue: user.firstName,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((user.firstName) = $event)),
                      rules: _unref(firstnameRules),
                      label: "First name",
                      "hide-details": "",
                      required: ""
                    }, null, 8, ["modelValue", "rules"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, { class: "mx-auto" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_text_field, {
                      modelValue: user.lastName,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((user.lastName) = $event)),
                      rules: _unref(lastnameRules),
                      label: "Last name",
                      "hide-details": "",
                      required: ""
                    }, null, 8, ["modelValue", "rules"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, { class: "mx-auto" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_text_field, {
                      modelValue: user.email,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((user.email) = $event)),
                      label: "Email",
                      required: ""
                    }, null, 8, ["modelValue"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, { class: "mx-auto" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_select, {
                      modelValue: user.level,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((user.level) = $event)),
                      rules: _unref(levelRules),
                      items: [
              { title: 'Device Registration', value: _unref(UserLevel).Device },
              { title: 'User Registration', value: _unref(UserLevel).RegisteredUser },
              { title: 'Content Editor', value: _unref(UserLevel).ContentEditor },
              { title: 'Admin', value: _unref(UserLevel).Admin },
            ],
                      label: "Level",
                      required: ""
                    }, null, 8, ["modelValue", "rules", "items"])
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_row, null, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, { col: "2" }),
                (user.id !== -1)
                  ? (_openBlock(), _createBlock(_component_v_col, { key: 0 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          class: "mt-2",
                          color: "warning",
                          onClick: deleteUser,
                          block: ""
                        }, {
                          default: _withCtx(() => _cache[6] || (_cache[6] = [
                            _createTextVNode("Delete")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_v_col, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      class: "mt-2",
                      color: "warning",
                      block: "",
                      onClick: resetAll
                    }, {
                      default: _withCtx(() => _cache[7] || (_cache[7] = [
                        _createTextVNode("Reset All")
                      ])),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                (user.id !== -1)
                  ? (_openBlock(), _createBlock(_component_v_col, { key: 1 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          class: "mt-2",
                          type: "button",
                          onClick: resetPassword,
                          block: ""
                        }, {
                          default: _withCtx(() => _cache[8] || (_cache[8] = [
                            _createTextVNode("Reset Password")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (user.id !== -1 && user.twoFactorEnabled)
                  ? (_openBlock(), _createBlock(_component_v_col, { key: 2 }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          class: "mt-2",
                          color: "warning",
                          type: "button",
                          block: "",
                          onClick: disable2FA
                        }, {
                          default: _withCtx(() => _cache[9] || (_cache[9] = [
                            _createTextVNode("Disable 2FA")
                          ])),
                          _: 1
                        })
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_component_v_col, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      class: "mt-2",
                      color: "success",
                      type: "submit",
                      block: ""
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(action.value), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_col, { col: "2" }, {
                  default: _withCtx(() => [
                    (user.level === _unref(UserLevel).Admin && user.twoFactorEnabled)
                      ? (_openBlock(), _createBlock(_component_v_chip, {
                          key: 0,
                          class: "mt-3",
                          variant: "flat",
                          color: "info"
                        }, {
                          default: _withCtx(() => _cache[10] || (_cache[10] = [
                            _createTextVNode("2FA")
                          ])),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    (user.level === _unref(UserLevel).Admin && !user.twoFactorEnabled)
                      ? (_openBlock(), _createBlock(_component_v_chip, {
                          key: 1,
                          class: "mt-3",
                          variant: "flat",
                          color: "warning"
                        }, {
                          default: _withCtx(() => _cache[11] || (_cache[11] = [
                            _createTextVNode("No 2FA")
                          ])),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    (showResetPassword.value)
      ? (_openBlock(), _createBlock(_component_v_form, { key: 0 }, {
          default: _withCtx(() => [
            _createVNode(_component_v_container, null, {
              default: _withCtx(() => [
                _cache[13] || (_cache[13] = _createElementVNode("p", null, "Provide a new password for the user, and press \"reset\":", -1)),
                _createVNode(_component_v_row, null, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_col, { cols: "9" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_text_field, {
                          modelValue: password.value,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((password).value = $event)),
                          label: "New Password",
                          required: ""
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_col, null, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_btn, {
                          class: "mt-2",
                          type: "button",
                          color: "warning",
                          disabled: !password.value.length,
                          onClick: saveResetPassword,
                          block: ""
                        }, {
                          default: _withCtx(() => _cache[12] || (_cache[12] = [
                            _createTextVNode("Reset")
                          ])),
                          _: 1
                        }, 8, ["disabled"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (user.id !== -1)
      ? (_openBlock(), _createBlock(_component_v_container, { key: 1 }, {
          default: _withCtx(() => [
            (__props.data)
              ? (_openBlock(), _createBlock(UserLabels, {
                  key: 0,
                  user: __props.data
                }, null, 8, ["user"]))
              : _createCommentVNode("", true),
            (__props.data)
              ? (_openBlock(), _createBlock(UserCommunity, {
                  key: 1,
                  user: __props.data
                }, null, 8, ["user"]))
              : _createCommentVNode("", true),
            user
              ? (_openBlock(), _createBlock(UserNotifications, {
                  key: 2,
                  user: user
                }, null, 8, ["user"]))
              : _createCommentVNode("", true),
            user
              ? (_openBlock(), _createBlock(UserMessages, {
                  key: 3,
                  user: user
                }, null, 8, ["user"]))
              : _createCommentVNode("", true),
            user
              ? (_openBlock(), _createBlock(UserGoals, {
                  key: 4,
                  user: user
                }, null, 8, ["user"]))
              : _createCommentVNode("", true),
            user
              ? (_openBlock(), _createBlock(UserBPT, {
                  key: 5,
                  user: user,
                  onReset: resetBPT
                }, null, 8, ["user"]))
              : _createCommentVNode("", true),
            _createVNode(UserStories, { user: user }, null, 8, ["user"]),
            _createVNode(UserActions, { user: user }, null, 8, ["user"]),
            _createVNode(UserInitiatives, { user: user }, null, 8, ["user"]),
            _createVNode(UserInfoGraphics, { user: user }, null, 8, ["user"]),
            (__props.data)
              ? (_openBlock(), _createBlock(UserCo2Quiz, {
                  key: 6,
                  user: __props.data
                }, null, 8, ["user"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})